import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Container, Box, Typography, Paper, Stack, Chip,
  Accordion, AccordionSummary, AccordionDetails,
  Button, IconButton, CircularProgress, Alert,
  Dialog, DialogContent, Snackbar,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CloudDownload as DownloadIcon,
  Calculate as SolveIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { formatInTimeZone } from 'date-fns-tz';
import FileBreadcrumb from '../../components/files/FileBreadcrumb';
import DownloadProgressDialog from '../../components/files/DownloadProgressDialog';
import FileGrid from '../../components/files/FileGrid';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useFileDownload } from '../../hooks/useFileDownload';
import { useFileWebSocket } from '../../hooks/useFileWebSocket';
import { useAuth } from '../../contexts/AuthContext';
import { getAccessToken } from '../../services/authStorage';

const API_BASE_URL = '/api/v1';

const formatDate = (date) => {
  if (!date) return 'N/A';
  try {
    return formatInTimeZone(new Date(date), 'UTC', 'yyyy-MM-dd HH:mm') + ' UTC';
  } catch { return 'N/A'; }
};

const TargetFiles = () => {
  const { targetName: encodedTargetName } = useParams();
  const targetName = decodeURIComponent(encodedTargetName);
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('project');
  const { user } = useAuth();
  const isAdmin = user?.role === 'server_admin' || user?.role === 'admin' || user?.role === 'instructor';
  const { downloadFiles, downloadGroup, downloadProgress, cancelDownload, getDownloadUrl, downloadCalibrated } = useFileDownload();
  const [calibrateError, setCalibrateError] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [files, setFiles] = useState([]);
  const [masterCoverage, setMasterCoverage] = useState({});
  const [projectName, setProjectName] = useState('');
  const [previewDialog, setPreviewDialog] = useState({ open: false, url: null, name: null });
  const [expandedObservations, setExpandedObservations] = useState(new Set());
  const [confirm, setConfirm] = useState(null);

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getAccessToken();
      const response = await axios.get(`${API_BASE_URL}/user-files/group-files`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { groupType: 'target', groupId: targetName }
      });

      if (response.data.success) {
        setFiles(response.data.data.files || []);
        setMasterCoverage(response.data.data.master_coverage || {});
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load target files');
    } finally {
      setLoading(false);
    }
  }, [targetName]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  // Get project name if we have a project context
  useEffect(() => {
    if (!projectId) return;
    const fetchProjectName = async () => {
      try {
        const token = getAccessToken();
        const response = await axios.get(`${API_BASE_URL}/user-files/by-project`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          const project = response.data.data.groups?.find(g => g._id?.toString() === projectId);
          if (project) setProjectName(project.project_name);
        }
      } catch { /* ignore */ }
    };
    fetchProjectName();
  }, [projectId]);

  const handleFileUpdate = useCallback((update) => {
    setFiles(prevFiles =>
      prevFiles.map(file => {
        const fileId = file.file_id || file._id;
        if (fileId === update.file_id) {
          return { ...file, plate_solve_status: update.plate_solve_status, plate_solve_result: update.plate_solve_result || file.plate_solve_result };
        }
        return file;
      })
    );
  }, []);

  useFileWebSocket(handleFileUpdate);

  const handleSolveFile = async (fileId) => {
    try {
      const token = getAccessToken();
      await axios.post(`${API_BASE_URL}/user-files/${fileId}/plate-solve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch { alert('Failed to queue plate solving job'); }
  };

  const handleDeleteFile = (fileId, filename) => {
    setConfirm({
      title: 'Delete File',
      message: <span>Are you sure you want to delete <strong>{filename}</strong>?</span>,
      confirmLabel: 'Delete',
      confirmColor: 'error',
      confirmIcon: <DeleteIcon />,
      icon: <DeleteIcon color="error" />,
      onConfirm: async () => {
        try {
          const token = getAccessToken();
          await axios.delete(`${API_BASE_URL}/user-files/${fileId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setFiles(prev => prev.filter(f => (f.file_id || f._id) !== fileId));
        } catch (err) {
          setCalibrateError('Failed to delete file');
          throw err;
        }
      }
    });
  };

  const handleSolveAllUnsolved = async (observationFiles) => {
    const unsolved = observationFiles.filter(f => f.plate_solve_result?.solved !== true);
    if (unsolved.length === 0) {
      alert('All files are already solved!');
      return;
    }
    setConfirm({
      title: 'Queue Plate Solving',
      message: <span>Queue plate solving for <strong>{unsolved.length}</strong> unsolved file(s)?</span>,
      confirmLabel: 'Queue',
      confirmColor: 'primary',
      confirmIcon: <SolveIcon />,
      icon: <SolveIcon color="primary" />,
      onConfirm: async () => {
        const token = getAccessToken();
        let success = 0;
        for (const file of unsolved) {
          try {
            await axios.post(`${API_BASE_URL}/user-files/${file.file_id || file._id}/plate-solve`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
            success++;
          } catch { /* continue */ }
        }
        alert(`Queued ${success} file(s) for plate solving.`);
      }
    });
  };

  const canDeleteFile = useCallback((file) => {
    return isAdmin || file.user_id === user?._id;
  }, [isAdmin, user]);

  const getCalibratedDownloadUrl = useCallback((fileId) => {
    const token = getAccessToken();
    return `/api/v1/calibration/download/${fileId}?format=calibrated&token=${encodeURIComponent(token)}`;
  }, []);

  const toggleSet = (setter, key) => {
    setter(prev => {
      const s = new Set(prev);
      s.has(key) ? s.delete(key) : s.add(key);
      return s;
    });
  };

  // Group files by observation
  const observationGroups = files.reduce((acc, file) => {
    const obsId = file.observation_id?.toString() || 'unknown';
    if (!acc[obsId]) {
      acc[obsId] = { files: [], name: file.observation_name || obsId };
    }
    acc[obsId].files.push(file);
    return acc;
  }, {});

  const sortedObservations = Object.entries(observationGroups)
    .sort(([, a], [, b]) => {
      const dateA = a.files[0]?.uploaded_at || '';
      const dateB = b.files[0]?.uploaded_at || '';
      return dateB.localeCompare(dateA);
    });

  // Auto-expand when few observations
  useEffect(() => {
    if (sortedObservations.length > 0 && expandedObservations.size === 0 && sortedObservations.length <= 5) {
      setExpandedObservations(new Set(sortedObservations.map(([id]) => id)));
    }
  }, [files.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build breadcrumb
  const breadcrumbItems = [{ label: 'Files', path: '/files' }];
  if (projectId && projectName) {
    breadcrumbItems.push({ label: projectName, path: `/files/project/${projectId}` });
  }
  breadcrumbItems.push({ label: targetName });

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        <FileBreadcrumb items={breadcrumbItems} />

        <Typography variant="h5" gutterBottom>{targetName}</Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          {sortedObservations.length} observation{sortedObservations.length !== 1 ? 's' : ''} &middot; {files.length} file{files.length !== 1 ? 's' : ''}
        </Typography>

        {/* Bulk Actions */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={() => downloadGroup('target', targetName)}
            disabled={files.length === 0}
          >
            Download All ({files.length})
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<SolveIcon />}
            onClick={() => handleSolveAllUnsolved(files)}
            disabled={files.length === 0}
          >
            Solve Unsolved
          </Button>
        </Box>

        {/* Observation Accordions */}
        <Stack spacing={2}>
          {sortedObservations.map(([obsId, obsData]) => {
            const obsSolved = obsData.files.filter(f => f.plate_solve_result?.solved === true).length;

            return (
              <Accordion
                key={obsId}
                expanded={expandedObservations.has(obsId)}
                onChange={() => toggleSet(setExpandedObservations, obsId)}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6">{obsData.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(obsData.files[0]?.uploaded_at)}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip label={`${obsData.files.length} files`} size="small" />
                      {obsSolved > 0 && (
                        <Chip label={`${obsSolved} solved`} size="small" color="success" variant="outlined" />
                      )}
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => { e.stopPropagation(); downloadFiles(obsData.files.map(f => f.file_id || f._id)); }}
                        title="Download observation files"
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 2, py: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      startIcon={<SolveIcon />}
                      onClick={() => handleSolveAllUnsolved(obsData.files)}
                    >
                      Solve Unsolved
                    </Button>
                  </Box>
                  <FileGrid
                    files={obsData.files}
                    onPreview={(url, name) => setPreviewDialog({ open: true, url, name })}
                    onSolve={handleSolveFile}
                    getDownloadUrl={getDownloadUrl}
                    getCalibratedDownloadUrl={getCalibratedDownloadUrl}
                    downloadCalibrated={downloadCalibrated}
                    onCalibrateError={setCalibrateError}
                    masterCoverage={masterCoverage}
                    onDelete={handleDeleteFile}
                    canDelete={canDeleteFile}
                  />
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>

        {files.length === 0 && !loading && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="textSecondary">No files found for this target</Typography>
          </Paper>
        )}
      </Box>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, url: null, name: null })}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}
      >
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.default'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>{previewDialog.name || 'Preview'}</Typography>
          <IconButton onClick={() => setPreviewDialog({ open: false, url: null, name: null })} size="small"><CloseIcon /></IconButton>
        </Box>
        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', bgcolor: '#0a0a0a', minHeight: '400px', maxHeight: '85vh' }}>
          {previewDialog.url && (
            <img src={previewDialog.url} alt={previewDialog.name} style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain' }}
              onError={(e) => { e.target.style.display = 'none'; }} />
          )}
        </DialogContent>
      </Dialog>

      <DownloadProgressDialog progress={downloadProgress} onClose={cancelDownload} />

      <Snackbar
        open={Boolean(calibrateError)}
        autoHideDuration={8000}
        onClose={() => setCalibrateError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setCalibrateError(null)} severity="warning" variant="filled" sx={{ width: '100%' }}>
          {calibrateError}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        confirmColor={confirm?.confirmColor}
        confirmIcon={confirm?.confirmIcon}
        icon={confirm?.icon}
        onConfirm={confirm?.onConfirm}
        onClose={() => setConfirm(null)}
      />
    </Container>
  );
};

export default TargetFiles;
